import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator } from "lucide-react";

/**
 * Razorpay revenue-vs-fees calculator. Master-only (rendered inside
 * AdminSubscription which is already gated to is_master).
 *
 * Razorpay published fee schedule (Feb 2026) used here:
 *   - UPI Autopay (subscriptions on UPI mandate): 0.50% + 18% GST on fee
 *   - Domestic card (debit/credit) Subscription/eMandate: 2.00% + 18% GST on fee
 *
 * The calculator is intentionally simple — type your plan price and the % of
 * customers you expect to be on each payment method, and it shows: per
 * transaction fee, per month total fees, and net revenue across N tenants.
 * If Razorpay revises fees, edit FEE_RATES below.
 */
const FEE_RATES = {
  upi:  { label: "UPI Autopay", rate: 0.005, gst: 0.18, hint: "Most common in B2B India" },
  card: { label: "Card / eMandate", rate: 0.020, gst: 0.18, hint: "Debit, credit, net-banking" },
};

const fmtINR = (n) => `INR ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const computePerTxnFee = (amount, method) => {
  const cfg = FEE_RATES[method];
  if (!cfg) return 0;
  const base = amount * cfg.rate;
  return base + base * cfg.gst;
};

export const RazorpayFeesCalculator = () => {
  // Defaults: ₹999 Core, ₹2999 Pro, 5 Core + 1 Pro tenant, 80% UPI / 20% card.
  // Just placeholders — operator types in their actual numbers.
  const [pCore, setPCore] = useState("999");
  const [pPro, setPPro] = useState("2999");
  const [nCore, setNCore] = useState("5");
  const [nPro, setNPro] = useState("1");
  const [pctUpi, setPctUpi] = useState("80");

  const r = useMemo(() => {
    const priceCore = Math.max(0, parseFloat(pCore) || 0);
    const pricePro = Math.max(0, parseFloat(pPro) || 0);
    const tenantsCore = Math.max(0, parseInt(nCore) || 0);
    const tenantsPro = Math.max(0, parseInt(nPro) || 0);
    const upi = Math.min(100, Math.max(0, parseFloat(pctUpi) || 0)) / 100;
    const card = 1 - upi;

    // Per-tenant per-month fee, blended across methods
    const feePerCore = computePerTxnFee(priceCore, "upi") * upi + computePerTxnFee(priceCore, "card") * card;
    const feePerPro = computePerTxnFee(pricePro, "upi") * upi + computePerTxnFee(pricePro, "card") * card;

    const grossMonth = priceCore * tenantsCore + pricePro * tenantsPro;
    const feeMonth = feePerCore * tenantsCore + feePerPro * tenantsPro;
    const netMonth = grossMonth - feeMonth;

    return {
      grossMonth, feeMonth, netMonth,
      grossYear: grossMonth * 12,
      feeYear: feeMonth * 12,
      netYear: netMonth * 12,
      feePerCore, feePerPro,
      // Sample per-txn breakdown for the price-point table below
      breakdownCore: {
        upi: computePerTxnFee(priceCore, "upi"),
        card: computePerTxnFee(priceCore, "card"),
      },
      breakdownPro: {
        upi: computePerTxnFee(pricePro, "upi"),
        card: computePerTxnFee(pricePro, "card"),
      },
    };
  }, [pCore, pPro, nCore, nPro, pctUpi]);

  return (
    <Card className="bg-gray-900/60 border-cyan-500/30" data-testid="razorpay-fees-calculator">
      <CardHeader>
        <CardTitle className="text-cyan-300 flex items-center gap-2">
          <Calculator className="h-5 w-5" />
          Razorpay Revenue Calculator
        </CardTitle>
        <p className="text-xs text-gray-500">
          Model what you'll actually receive after Razorpay's per-transaction
          fees + GST. Type your numbers below — the cells update live.
          Razorpay charges <span className="text-cyan-300">0.5% on UPI Autopay</span> and <span className="text-cyan-300">2% on cards</span>, both + 18% GST. No fixed monthly fee.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Inputs grid */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <Label className="text-gray-400 text-xs">Core price / month</Label>
            <Input type="number" value={pCore} onChange={(e) => setPCore(e.target.value)} className="bg-gray-800 border-gray-700 text-white mt-1" data-testid="calc-core-price" />
          </div>
          <div>
            <Label className="text-gray-400 text-xs">Pro price / month</Label>
            <Input type="number" value={pPro} onChange={(e) => setPPro(e.target.value)} className="bg-gray-800 border-gray-700 text-white mt-1" data-testid="calc-pro-price" />
          </div>
          <div>
            <Label className="text-gray-400 text-xs"># Core tenants</Label>
            <Input type="number" value={nCore} onChange={(e) => setNCore(e.target.value)} className="bg-gray-800 border-gray-700 text-white mt-1" data-testid="calc-core-count" />
          </div>
          <div>
            <Label className="text-gray-400 text-xs"># Pro tenants</Label>
            <Input type="number" value={nPro} onChange={(e) => setNPro(e.target.value)} className="bg-gray-800 border-gray-700 text-white mt-1" data-testid="calc-pro-count" />
          </div>
          <div>
            <Label className="text-gray-400 text-xs">% paying via UPI</Label>
            <Input type="number" min="0" max="100" value={pctUpi} onChange={(e) => setPctUpi(e.target.value)} className="bg-gray-800 border-gray-700 text-white mt-1" data-testid="calc-upi-pct" />
            <p className="text-[10px] text-gray-500 mt-1">Remaining {100 - (parseFloat(pctUpi) || 0)}% on card / eMandate</p>
          </div>
        </div>

        {/* Headline numbers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Stat label="Gross / month" value={fmtINR(r.grossMonth)} tone="text-gray-200" testId="calc-gross-month" />
          <Stat label="Razorpay fees + GST / month" value={`− ${fmtINR(r.feeMonth)}`} tone="text-amber-300" testId="calc-fees-month" />
          <Stat label="Net to your bank / month" value={fmtINR(r.netMonth)} tone="text-emerald-300" testId="calc-net-month" highlight />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Stat label="Gross / year" value={fmtINR(r.grossYear)} tone="text-gray-300 text-sm" />
          <Stat label="Fees / year" value={`− ${fmtINR(r.feeYear)}`} tone="text-amber-200 text-sm" />
          <Stat label="Net / year" value={fmtINR(r.netYear)} tone="text-emerald-200 text-sm" testId="calc-net-year" />
        </div>

        {/* Per-transaction breakdown — helps the operator sanity-check rates */}
        <div>
          <p className="text-gray-400 text-xs mb-2">Per-transaction fee breakdown (single tenant, one cycle):</p>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="bg-gray-800/40 border border-gray-700 rounded p-3">
              <p className="text-gray-300 mb-1">Core ({fmtINR(pCore)})</p>
              <div className="flex justify-between text-gray-400"><span>UPI Autopay (0.5% + GST):</span><span className="text-emerald-300">{fmtINR(r.breakdownCore.upi)}</span></div>
              <div className="flex justify-between text-gray-400"><span>Card / eMandate (2% + GST):</span><span className="text-amber-300">{fmtINR(r.breakdownCore.card)}</span></div>
            </div>
            <div className="bg-gray-800/40 border border-gray-700 rounded p-3">
              <p className="text-gray-300 mb-1">Pro ({fmtINR(pPro)})</p>
              <div className="flex justify-between text-gray-400"><span>UPI Autopay (0.5% + GST):</span><span className="text-emerald-300">{fmtINR(r.breakdownPro.upi)}</span></div>
              <div className="flex justify-between text-gray-400"><span>Card / eMandate (2% + GST):</span><span className="text-amber-300">{fmtINR(r.breakdownPro.card)}</span></div>
            </div>
          </div>
        </div>

        <p className="text-[10px] text-gray-500 leading-relaxed">
          Estimate only. Razorpay fee schedules can be revised; international
          cards, EMI, and wallets carry different rates than the two used
          here. Refund fees, instant-settlement opt-in, and dispute fees (₹100
          fixed) are excluded. GST paid is recoverable as Input Tax Credit if
          you're GST-registered.
        </p>
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value, tone = "text-gray-200", testId, highlight = false }) => (
  <div className={`bg-gray-800/50 border ${highlight ? "border-emerald-500/40" : "border-gray-700"} rounded p-3`} data-testid={testId}>
    <p className="text-gray-400 text-xs">{label}</p>
    <p className={`mt-1 font-semibold ${tone}`}>{value}</p>
  </div>
);

export default RazorpayFeesCalculator;
